/**
 * Парсер FastAPI 422 validation detail в map `{field_name: message}`.
 *
 * Backend отдаёт `detail: [{loc: ['body','title'], msg: '...', type: '...'}, ...]`.
 * Берём `loc[loc.length-1]` как имя поля и кладём `msg`. Если `allowedFields`
 * передан — фильтруем; иначе пускаем все.
 *
 * Используется admin editor'ами AB3 Academy и любым другим UI, который
 * рендерит inline field errors.
 */
export function parseFastApiValidationErrors<K extends string = string>(
  detail: unknown,
  allowedFields?: readonly K[],
): Partial<Record<K, string>> {
  const out: Partial<Record<K, string>> = {};
  if (!Array.isArray(detail)) return out;
  const allowed = allowedFields ? new Set<string>(allowedFields) : null;
  for (const item of detail) {
    if (!item || typeof item !== 'object') continue;
    const loc = (item as { loc?: unknown[] }).loc;
    const msg = (item as { msg?: string }).msg;
    if (!Array.isArray(loc) || typeof msg !== 'string') continue;
    const field = String(loc[loc.length - 1]);
    if (allowed && !allowed.has(field)) continue;
    (out as Record<string, string>)[field] = msg;
  }
  return out;
}

export enum ErrorType {
  NETWORK = 'NETWORK_ERROR',
  AUTHENTICATION = 'AUTHENTICATION_ERROR',
  AUTHORIZATION = 'AUTHORIZATION_ERROR',
  VALIDATION = 'VALIDATION_ERROR',
  SERVER = 'SERVER_ERROR',
  UNKNOWN = 'UNKNOWN_ERROR',
  // AB3 Academy Phase 2: специальные коды для sandbox-flow.
  // 410 = TTL вышел или watchdog дропнул схему → recovery UI.
  // 400 = битый/подделанный X-Sandbox-Session → log + redirect.
  SANDBOX_EXPIRED = 'SANDBOX_EXPIRED',
  SANDBOX_INVALID = 'SANDBOX_INVALID',
}

export interface AppError {
  type: ErrorType;
  message: string;
  code?: string;
  details?: any;
  statusCode?: number;
}

export class ErrorHandler {
  private static instance: ErrorHandler;
  private errorListeners: ((error: AppError) => void)[] = [];

  private constructor() {}

  static getInstance(): ErrorHandler {
    if (!ErrorHandler.instance) {
      ErrorHandler.instance = new ErrorHandler();
    }
    return ErrorHandler.instance;
  }

  /**
   * Подписаться на ошибки
   */
  subscribe(listener: (error: AppError) => void): () => void {
    this.errorListeners.push(listener);
    return () => {
      this.errorListeners = this.errorListeners.filter((l) => l !== listener);
    };
  }

  /**
   * Обработать ошибку от API
   */
  handleApiError(error: any): AppError {
    let appError: AppError;

    if (error.response) {
      // Ошибка от сервера
      const status = error.response.status;
      const data = error.response.data;

      switch (status) {
        case 401:
          appError = {
            type: ErrorType.AUTHENTICATION,
            message: data?.message || 'Необходима авторизация',
            statusCode: status,
            details: data,
          };
          break;
        case 403:
          appError = {
            type: ErrorType.AUTHORIZATION,
            message: data?.message || 'Недостаточно прав',
            statusCode: status,
            details: data,
          };
          break;
        case 400:
        case 422:
          appError = {
            type: ErrorType.VALIDATION,
            message: data?.message || 'Некорректные данные',
            statusCode: status,
            details: data?.errors || data,
          };
          break;
        case 500:
        case 502:
        case 503:
          appError = {
            type: ErrorType.SERVER,
            message: data?.message || 'Ошибка сервера',
            statusCode: status,
            details: data,
          };
          break;
        default:
          appError = {
            type: ErrorType.UNKNOWN,
            message: data?.message || `Ошибка: ${status}`,
            statusCode: status,
            details: data,
          };
      }
    } else if (error.request) {
      // Ошибка сети
      appError = {
        type: ErrorType.NETWORK,
        message: 'Ошибка соединения. Проверьте интернет.',
        details: error.message,
      };
    } else {
      // Другая ошибка
      appError = {
        type: ErrorType.UNKNOWN,
        message: error.message || 'Произошла неизвестная ошибка',
        details: error,
      };
    }

    this.notifyListeners(appError);
    return appError;
  }

  /**
   * Обработать ошибку fetch
   */
  async handleFetchError(response: Response): Promise<AppError> {
    let message = 'Произошла ошибка';
    let details: any = null;

    try {
      const data = await response.json();
      message = data.message || data.error || message;
      details = data;
    } catch {
      // Не удалось распарсить JSON
      message = `Ошибка: ${response.status} ${response.statusText}`;
    }

    const appError: AppError = {
      type: this.getErrorTypeByStatus(response.status),
      message,
      statusCode: response.status,
      details,
    };

    this.notifyListeners(appError);
    return appError;
  }

  /**
   * Обработать общую ошибку
   */
  handleError(error: Error | AppError | any): AppError {
    let appError: AppError;

    if (this.isAppError(error)) {
      appError = error;
    } else if (error instanceof Error) {
      appError = {
        type: ErrorType.UNKNOWN,
        message: error.message,
        details: error.stack,
      };
    } else {
      appError = {
        type: ErrorType.UNKNOWN,
        message: String(error),
        details: error,
      };
    }

    this.notifyListeners(appError);
    return appError;
  }

  /**
   * Получить читаемое сообщение об ошибке
   */
  getErrorMessage(error: AppError): string {
    switch (error.type) {
      case ErrorType.NETWORK:
        return 'Проблема с соединением. Проверьте интернет и попробуйте снова.';
      case ErrorType.AUTHENTICATION:
        return 'Необходимо войти в систему.';
      case ErrorType.AUTHORIZATION:
        return 'У вас недостаточно прав для выполнения этого действия.';
      case ErrorType.VALIDATION:
        if (error.details && typeof error.details === 'object') {
          const errors = Object.values(error.details).flat();
          if (errors.length > 0) {
            return errors.join(', ');
          }
        }
        return error.message;
      case ErrorType.SERVER:
        return 'Ошибка на сервере. Попробуйте позже.';
      default:
        return error.message || 'Произошла неизвестная ошибка.';
    }
  }

  private getErrorTypeByStatus(status: number): ErrorType {
    if (status === 401) return ErrorType.AUTHENTICATION;
    if (status === 403) return ErrorType.AUTHORIZATION;
    if (status === 400 || status === 422) return ErrorType.VALIDATION;
    if (status >= 500) return ErrorType.SERVER;
    return ErrorType.UNKNOWN;
  }

  private isAppError(error: any): error is AppError {
    return error && typeof error === 'object' && 'type' in error && 'message' in error;
  }

  private notifyListeners(error: AppError): void {
    this.errorListeners.forEach((listener) => {
      try {
        listener(error);
      } catch (e) {
        console.error('Error in error listener:', e);
      }
    });
  }
}

export const errorHandler = ErrorHandler.getInstance();